import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const pillars = [
  {
    icon: 'ri-eye-line',
    title: 'Our Vision',
    text: 'To become the most trusted technology partner for companies building the future — where every team has access to world-class engineering talent, regardless of borders.',
    accent: 'primary',
  },
  {
    icon: 'ri-focus-3-line',
    title: 'Our Mission',
    text: 'Connecting visionary companies with world-class engineering talent and delivering software that moves markets. We exist to turn ambitious ideas into shipped products.',
    accent: 'emerald',
  },
];

const values = [
  { icon: 'ri-heart-3-line', title: 'People First', desc: 'We invest in people — our team, our clients, and the engineers we place. Relationships drive everything we do.' },
  { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'Transparent processes, honest communication, and ethical partnerships. No hidden fees, no inflated resumes.' },
  { icon: 'ri-lightbulb-flash-line', title: 'Innovation', desc: 'Embracing emerging tech to keep our clients ahead of the curve. We experiment so you do not have to.' },
  { icon: 'ri-global-line', title: 'Global Mindset', desc: 'Diversity and cross-border collaboration drive better outcomes. Talent has no zip code.' },
  { icon: 'ri-speed-line', title: 'Speed & Quality', desc: 'Fast delivery without compromising engineering excellence. We measure twice and code once.' },
  { icon: 'ri-customer-service-2-line', title: 'Partnership', desc: 'We work alongside you, not just for you. Your success metrics are our success metrics.' },
];

export default function VisionMissionValues() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-compass-3-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">What Drives Us</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Vision, Mission
            <br />
            <span className="text-primary-600">& Core Values.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            The principles that guide every decision we make and every product we build.
          </motion.p>
        </motion.div>

        {/* Vision & Mission Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ ...springTransition, duration: 0.7, delay: index * 0.12 }}
              className={`group relative bg-white rounded-2xl p-6 md:p-8 border border-gray-100 hover:border-${pillar.accent}-300 transition-all duration-500 hover:shadow-lg`}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: index * 0.12 + 0.1, ...springTransition }}
                className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center ${pillar.accent === 'emerald' ? 'bg-emerald-500' : 'bg-primary-500'} rounded-2xl shadow-lg mb-5 md:mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500`}
              >
                <i className={`${pillar.icon} text-2xl md:text-3xl text-white`}></i>
              </motion.div>
              <motion.h3
                initial={{ opacity: 0, y: 15 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.12 + 0.15, duration: 0.5 }}
                className="text-xl md:text-2xl font-bold text-navy-900 mb-3"
              >
                {pillar.title}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.12 + 0.2, duration: 0.5 }}
                className="text-base text-gray-600 leading-relaxed"
              >
                {pillar.text}
              </motion.p>
            </motion.div>
          ))}
        </div>

        {/* Core Values Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-center mb-8 md:mb-10"
        >
          <h3 className="text-2xl md:text-3xl font-display font-bold text-navy-900">Our Core Values</h3>
          <div className="w-16 h-1 bg-primary-500 rounded-full mx-auto mt-3"></div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ ...springTransition, duration: 0.6, delay: 0.5 + index * 0.07 }}
              className="group relative bg-gray-50 rounded-xl p-5 md:p-6 border border-gray-100 hover:border-primary-200 hover:bg-white transition-all duration-400 hover:shadow-md"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-primary-500 rounded-xl shadow-md flex-shrink-0 group-hover:scale-110 transition-transform duration-400">
                  <i className={`${value.icon} text-lg md:text-xl text-white`}></i>
                </div>
                <div>
                  <h4 className="text-base md:text-lg font-bold text-navy-900 mb-1">{value.title}</h4>
                  <p className="text-sm md:text-base text-gray-500 leading-relaxed">{value.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}